/**
 * Super-admin account-level report sync actions.
 *
 * Reuse this registry for any future “sync X reports for profile / all profiles”
 * controls on the Accounts page — do not add one-off buttons in AccountsPage.
 *
 * Backend idempotency (targets / keywords / campaigns share this pattern):
 * - `amazon_report_requests` unique on (profile_id, report_type_id, window_start, window_end)
 * - Fully imported windows are skipped (`is_fully_imported`)
 * - Active in-flight batches are reused (`reused_active_batch`)
 * - Fact upserts use ON CONFLICT DO UPDATE on grain keys
 *   (e.g. target rows: profile_id + ad_product + date + target_id)
 * So re-clicking Sync does not create duplicate metric rows.
 *
 * Amazon create_report is paced server-side (one-at-a-time with 429 backoff).
 * Clicking Sync enqueues remaining families/windows; the report poller resumes them.
 */

import { adsApi } from '@/api/client';

export type AccountAdminSyncMeta = {
  accountName?: string;
  managerAccountName?: string;
};

export type AccountAdminSyncKind = {
  /** Stable id used in UI state keys / analytics */
  id: string;
  /** Per-row button label */
  label: string;
  /** Master “all accounts” button label */
  masterLabel: string;
  /** Short panel heading */
  panelTitle: string;
  /** Help text under the master panel */
  description: string;
  /** Native tooltip on buttons */
  title: string;
  /** Enqueue Amazon report jobs for one profile (idempotent server-side). */
  enqueue: (profileId: string, meta?: AccountAdminSyncMeta) => Promise<void>;
};

/**
 * Workspace report families: campaigns, keywords, search terms, targets, placements.
 * Enqueues last-12-complete-calendar-month windows (plus yesterday); server skips
 * already-imported S3 slots and paces Amazon create_report one job at a time.
 */
export async function enqueueProfileReportSync(
  profileId: string,
  meta: AccountAdminSyncMeta = {}
): Promise<void> {
  const current = await adsApi.startProfileUnifiedReportSync(profileId, {
    accountName: meta.accountName,
    managerAccountName: meta.managerAccountName,
  });
  if (!current.success) {
    throw new Error('Report sync failed to start');
  }
}

/** @deprecated Use enqueueProfileReportSync */
export const enqueueTargetReportSync = enqueueProfileReportSync;

/**
 * Register new admin sync kinds here. AccountsPage renders from this list only.
 */
export const ACCOUNT_ADMIN_SYNC_KINDS: readonly AccountAdminSyncKind[] = [
  {
    id: 'reports',
    label: 'Sync',
    masterLabel: 'Sync all',
    panelTitle: 'Ads reports (super admin)',
    description:
      'Gap-fills the last 12 complete calendar months of Unified Reporting v1 (plus missing yesterday) from S3; Amazon create/retrieve runs one month at a time for missing windows. Safe to re-run.',
    title:
      'Enqueue Unified Reporting v1 monthly windows (last 12 complete calendar months). Existing S3 objects are skipped; Amazon is called only for gaps.',
    enqueue: enqueueProfileReportSync,
  },
] as const;

export type AccountAdminSyncKindId = (typeof ACCOUNT_ADMIN_SYNC_KINDS)[number]['id'];
